import { apiClient } from './api-client';

// Authorization Case Management System (docs/AUTHORIZATION_CASE_MANAGEMENT_DESIGN.md)

export type AuthorizationCurrentOwner = 'ENGINEER' | 'OPS' | 'COMMERCIAL' | 'ADMIN';
export type AuthorizationWaitingFor = 'ENGINEER' | 'OPS' | 'COMMERCIAL' | 'ADMIN' | 'CUSTOMER';
// Phase 7 Slice 4/5A (D-127 OD-3′): REQUEST_MODIFICATION exists for QUOTATION
// request kinds only, and only while a quotation revision awaits the customer —
// the server refuses it everywhere else. The UI offers it under that condition alone.
export type CustomerResponseType =
  'APPROVED' | 'REJECTED' | 'NEED_MORE_INFORMATION' | 'ASK_QUESTION' | 'REQUEST_MODIFICATION';
export type AuthorizationCommunicationMethod =
  'PORTAL' | 'PHONE' | 'WHATSAPP' | 'EMAIL' | 'PHYSICAL_VISIT' | 'VIDEO_CALL' | 'OTHER';

// Engineer-Controlled Evidence Selection (docs/reports/authorization/
// AUTHORIZATION_EVIDENCE_SELECTION_IMPLEMENTATION) — the Engineer chooses which
// existing inspection data supports the request; references only, never a new upload.
export type EvidenceSourceType = 'ATTACHMENT' | 'PROCEDURE_EVIDENCE' | 'FINDING' | 'NOTE';

export interface EvidenceSelectionItem {
  sourceType: EvidenceSourceType;
  sourceId: string;
}

/** Resolved display shape for one selected item — matches SelectedEvidenceItemDto. */
export interface SelectedEvidenceItem {
  id: string;
  sourceType: EvidenceSourceType;
  sourceId: string;
  selectedByName: string;
  createdAt: string;
  label: string;
  url: string | null;
  thumbnailUrl: string | null;
  mimeType: string | null;
  fileSizeBytes: string | null;
  mediaType: string | null;
  findingNumber: number | null;
  findingSeverity: string | null;
}

export const CURRENT_OWNER_LABELS: Record<AuthorizationCurrentOwner, string> = {
  ENGINEER: 'Engineer',
  OPS: 'Operations',
  COMMERCIAL: 'Commercial',
  ADMIN: 'Admin',
};

export const WAITING_FOR_LABELS: Record<AuthorizationWaitingFor, string> = {
  ENGINEER: 'Engineer',
  OPS: 'Operations',
  COMMERCIAL: 'Commercial',
  ADMIN: 'Admin',
  CUSTOMER: 'Customer',
};

export const CUSTOMER_RESPONSE_LABELS: Record<CustomerResponseType, string> = {
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  NEED_MORE_INFORMATION: 'Need More Information',
  ASK_QUESTION: 'Ask Question',
  REQUEST_MODIFICATION: 'Request Modification',
};

// Portal listed first — the default/preferred method (design §6).
export const COMMUNICATION_METHOD_LABELS: Record<AuthorizationCommunicationMethod, string> = {
  PORTAL: 'Customer Portal',
  PHONE: 'Phone Call',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Email',
  PHYSICAL_VISIT: 'Physical Visit',
  VIDEO_CALL: 'Video Call',
  OTHER: 'Other',
};

export interface AuthorizationTimelineEntry {
  id: string;
  type: 'HISTORY' | 'COMMUNICATION';
  actorName: string | null;
  actorRoleName: string | null;
  action: string | null;
  method: string | null;
  decision: string | null;
  summary: string | null;
  createdAt: string;
}

// ── Authorization Radar / Request Journey (D-112; design §3, §12.3, §16) ─────
// Stage 3 consumes the Stage 2 journey endpoint verbatim. Every field below is
// returned by the backend — nothing here is derived, defaulted or inferred on
// the client, and the frontend never reconstructs a journey of its own.

/** Why a journey could not be replayed onto the case's live state (design §16). */
export type AuthorizationJourneyIntegrityCode =
  | 'CONSISTENT'
  | 'NO_TRANSITIONS_RECORDED'
  | 'OWNER_MISMATCH'
  | 'WAITING_MISMATCH'
  | 'OWNER_AND_WAITING_MISMATCH';

export interface AuthorizationJourneyIntegrity {
  integrity: AuthorizationJourneyIntegrityCode;
  /** True only when the replayed end state equals the live state. */
  consistent: boolean;
  replayedOwner: AuthorizationCurrentOwner | null;
  replayedWaitingFor: AuthorizationWaitingFor | null;
  /** Always authoritative, regardless of `consistent`. */
  liveOwner: AuthorizationCurrentOwner;
  liveWaitingFor: AuthorizationWaitingFor;
}

/**
 * One persisted movement. A null on either side of a pair means that dimension
 * did not change — on the genesis hop, `fromOwner` is null because the case had
 * no prior owner. It is never a placeholder for an unknown value.
 */
export interface AuthorizationJourneyHop {
  id: string;
  type: 'HISTORY' | 'COMMUNICATION';
  /** AuthorizationAction — HISTORY entries only. */
  action: string | null;
  /** AuthorizationCommunicationMethod — COMMUNICATION entries only. */
  method: string | null;
  decision: string | null;
  actorName: string | null;
  actorRoleName: string | null;
  customerContactId: string | null;
  customerContactName: string | null;
  comment: string | null;
  fromOwner: AuthorizationCurrentOwner | null;
  toOwner: AuthorizationCurrentOwner | null;
  fromWaitingFor: AuthorizationWaitingFor | null;
  toWaitingFor: AuthorizationWaitingFor | null;
  /** True event time — may precede `createdAt` for communications (Rule E). */
  occurredAt: string;
  createdAt: string;
}

/**
 * Stage 5D — the DESCRIPTIVE expected-route layer, derived on read.
 *
 * `UNCONFIGURED` is a FIRST-CLASS state (design §6), never an error: the case
 * has no request type, no route, or no published version. `INTEGRITY_GAP` stops
 * everything — design §7.3 forbids showing a route beside a journey the system
 * cannot vouch for, because the juxtaposition would imply the route was followed.
 *
 * There is deliberately NO `nextStep` field: D-112 §1 forbids prediction, and
 * design §15 makes that absence a constraint rather than an oversight.
 */
export type AuthorizationRouteOutcome =
  'UNCONFIGURED' | 'INTEGRITY_GAP' | 'ON_ROUTE' | 'DEVIATED' | 'COMPLETE' | 'IN_PROGRESS';

export type AuthorizationExpectedStepClassification =
  'REACHED' | 'NOT_REACHED' | 'SKIPPED_MANDATORY';

export type AuthorizationExpectedHopClassification =
  'MATCH' | 'DEVIATION' | 'OVERRIDE' | 'RE_ENTRY' | 'CYCLE_MARKER';

export interface AuthorizationExpectedStep {
  stepId: string;
  sequence: number;
  classification: AuthorizationExpectedStepClassification;
  mandatory: boolean;
  terminal: boolean;
}

export interface AuthorizationExpectedHop {
  entryId: string;
  classification: AuthorizationExpectedHopClassification;
  stepId: string | null;
  sequence: number | null;
}

export interface AuthorizationExpectedRoute {
  outcome: AuthorizationRouteOutcome;
  /** The exact IMMUTABLE published version the case bound to at creation (D-119 §4). */
  routeVersionId: string | null;
  versionNumber: number | null;
  hops: AuthorizationExpectedHop[];
  steps: AuthorizationExpectedStep[];
}

export interface AuthorizationJourney {
  authorizationId: string;
  caseNumber: string;
  status: string;
  workflowStage: string;
  currentOwner: AuthorizationCurrentOwner;
  waitingFor: AuthorizationWaitingFor;
  /** EMPTY when `integrity.consistent` is false — a gap stays visible as a gap. */
  hops: AuthorizationJourneyHop[];
  integrity: AuthorizationJourneyIntegrity;
  /**
   * Stage 5D. Optional on the client only because a cached response predating
   * 5D carries none — the deployed API always sends it. When absent the expected
   * layer renders NOTHING rather than guessing, because "not sent" is not the
   * same claim as UNCONFIGURED.
   */
  expected?: AuthorizationExpectedRoute;
}

/**
 * Stage 5F — the per-row expected-route summary the Authorization Center shows.
 *
 * Every field is **computed server-side** inside the existing aggregate
 * (design §12.2): *"the client must never re-derive them, or two answers would
 * exist."* The list renders these values and calculates nothing.
 */
export interface AuthorizationRouteSummary {
  status: AuthorizationRouteOutcome;
  versionNumber: number | null;
  deviationCount: number;
  /** Structurally 0 until 5G1 exists — no hop can carry an override marker today. */
  overrideCount: number;
  customerGate: 'NONE' | 'REACHED' | 'NOT_REACHED' | 'SKIPPED_MANDATORY';
}

/** One row of the multi-case aggregate — design §12.1. */
export interface AuthorizationJourneyAggregateItem {
  id: string;
  caseNumber: string;
  inspectionId: string;
  inspectionNumber: string;
  /**
   * Equipment identity of THIS case's inspection (`inspection.equipment` on the
   * server). Non-nullable columns, but the empty string is possible and renders
   * with the table's existing em-dash convention.
   */
  equipmentAssetNumber: string;
  equipmentSerialNumber: string;
  priorityName: string | null;
  priorityColor: string | null;
  status: string;
  workflowStage: string;
  currentOwner: AuthorizationCurrentOwner;
  waitingFor: AuthorizationWaitingFor;
  requestedAt: string;
  waitingSinceAt: string;
  /** BOUNDED most-recent hops. Empty when the journey is inconsistent. */
  trail: AuthorizationJourneyHop[];
  totalHops: number;
  integrity: AuthorizationJourneyIntegrity;
  approvalRequestTypeName: string | null;
  customApprovalName: string | null;
  requestedByName: string;
  route: AuthorizationRouteSummary;
}

export interface AuthorizationJourneyAggregate {
  data: AuthorizationJourneyAggregateItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AuthorizationHistoryItem {
  id: string;
  actorId: string;
  actorName: string;
  actorRoleName: string;
  action: string;
  comment: string | null;
  createdAt: string;
}

export interface AuthorizationCase {
  id: string;
  tenantId: string;
  inspectionId: string;
  findingId: string | null;
  procedureStepExecutionId: string | null;
  caseNumber: string;
  priorityId: string | null;
  priorityName: string | null;
  priorityColor: string | null;
  currentOwner: AuthorizationCurrentOwner;
  waitingFor: AuthorizationWaitingFor;
  customerResponseType: CustomerResponseType | null;
  requestedById: string;
  requestedByName: string;
  respondedById: string | null;
  respondedByName: string | null;
  reason: string;
  responseNotes: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  workflowStage: string;
  /**
   * Private Workflow Visibility — who inside the `currentOwner` department class
   * is responsible now. NULL = unclaimed, sitting in that department's pool.
   */
  assignedUserId: string | null;
  approvalRequestTypeId: string | null;
  approvalRequestTypeName: string | null;
  /** ApprovalRequestType.description — why this approval type requires review. */
  approvalRequestTypeDescription?: string | null;
  isCustom: boolean;
  customApprovalName: string | null;
  requestedAt: string;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  history: AuthorizationHistoryItem[];
  timeline: AuthorizationTimelineEntry[];
  /** Evidence selected by Engineer — never the full inspection evidence set. */
  selectedEvidence?: SelectedEvidenceItem[];
}

export interface AuthorizationCaseListItem {
  id: string;
  caseNumber: string;
  inspectionId: string;
  inspectionNumber: string;
  approvalRequestTypeName: string | null;
  customApprovalName: string | null;
  priorityName: string | null;
  priorityColor: string | null;
  status: string;
  workflowStage: string;
  currentOwner: AuthorizationCurrentOwner;
  waitingFor: AuthorizationWaitingFor;
  requestedByName: string;
  requestedAt: string;
  waitingSinceAt: string;
}

export interface AuthorizationCaseListResponse {
  data: AuthorizationCaseListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface RecordCommunicationPayload {
  method: AuthorizationCommunicationMethod;
  customerContactId?: string;
  summary: string;
  decision?: CustomerResponseType;
}

export interface ReleaseCasePayload {
  action: 'APPROVED' | 'REJECTED';
  comment?: string;
}

export const authorizationCaseService = {
  list: (params: {
    page?: number;
    pageSize?: number;
    currentOwner?: AuthorizationCurrentOwner;
    waitingFor?: AuthorizationWaitingFor;
    status?: string;
    priorityId?: string;
  }): Promise<AuthorizationCaseListResponse> =>
    apiClient
      .get<AuthorizationCaseListResponse>('/authorization-cases', { params })
      .then((r) => r.data),

  get: (id: string): Promise<AuthorizationCase> =>
    apiClient.get<AuthorizationCase>(`/authorization-cases/${id}`).then((r) => r.data),

  /**
   * Authorization Radar — the merged, self-verifying journey for ONE case.
   *
   * A single request returns the whole journey already merged from both sources
   * and already verified against the case's live state. The radar must never
   * issue one request per hop, and must never rebuild the journey client-side.
   */
  getJourney: (id: string): Promise<AuthorizationJourney> =>
    apiClient.get<AuthorizationJourney>(`/authorization-cases/${id}/journey`).then((r) => r.data),

  /**
   * Stage 5F — the multi-case aggregate, extended with per-row route status.
   *
   * Rule O: ONE request for the whole page. The server issues a FIXED six
   * queries regardless of page size, so the client must never fan out per row,
   * and must never re-derive route status from the trail.
   */
  listJourney: (params: {
    page?: number;
    pageSize?: number;
    currentOwner?: AuthorizationCurrentOwner;
    waitingFor?: AuthorizationWaitingFor;
    status?: string;
    priorityId?: string;
  }): Promise<AuthorizationJourneyAggregate> =>
    apiClient
      .get<AuthorizationJourneyAggregate>('/authorization-cases/journey', { params })
      .then((r) => r.data),

  /** Take an unclaimed case out of the department intake pool. */
  claim: (id: string): Promise<AuthorizationCase> =>
    apiClient.post<AuthorizationCase>(`/authorization-cases/${id}/claim`).then((r) => r.data),

  /** Return a claimed case to the department intake pool (assignee only). */
  unclaim: (id: string): Promise<AuthorizationCase> =>
    apiClient.post<AuthorizationCase>(`/authorization-cases/${id}/unclaim`).then((r) => r.data),

  forwardToCommercial: (id: string, comment?: string): Promise<AuthorizationCase> =>
    apiClient
      .post<AuthorizationCase>(`/authorization-cases/${id}/forward-to-commercial`, { comment })
      .then((r) => r.data),

  forwardToAdmin: (id: string, comment?: string): Promise<AuthorizationCase> =>
    apiClient
      .post<AuthorizationCase>(`/authorization-cases/${id}/forward-to-admin`, { comment })
      .then((r) => r.data),

  recordCommunication: (
    id: string,
    payload: RecordCommunicationPayload,
  ): Promise<AuthorizationCase> =>
    apiClient
      .post<AuthorizationCase>(`/authorization-cases/${id}/communications`, payload)
      .then((r) => r.data),

  release: (id: string, payload: ReleaseCasePayload): Promise<AuthorizationCase> =>
    apiClient
      .post<AuthorizationCase>(`/authorization-cases/${id}/release`, payload)
      .then((r) => r.data),

  returnToOps: (id: string, comment?: string): Promise<AuthorizationCase> =>
    apiClient
      .patch<AuthorizationCase>(`/authorization-cases/${id}/return-to-ops`, { comment })
      .then((r) => r.data),
};
